import React, { useState } from "react";
import {
    ScheduleGrid,
    ContactCard,
    ContactCardHeader,
    ContactNumber,
    StatusChip,
    ContactCardBody,
    ContactCardFooter,
    SmallActionBtn,
    TimelineContainer,
    TimelineItem,
    TimelineCard,
    ProgressSection,
    ProgressLabel,
    ProgressBarOuter,
    ProgressBarInner,
    NextContactBanner,
    NextContactIcon,
    NextContactInfo,
    NextContactAction,
    ViewToggle,
    ViewToggleBtn,
    Legend,
    LegendItem,
    EmptyState,
    LoadingWrapper,
} from "./ANCScheduleStyles";

/* ─── Status display helpers ─── */
const STATUS_LABELS = {
    attended: "Attended",
    upcoming: "Upcoming",
    missed: "Missed",
    future: "Scheduled",
    rescheduled: "Rescheduled",
};

const STATUS_ICONS = {
    attended: "✓",
    upcoming: "⏱",
    missed: "✕",
    future: "○",
    rescheduled: "↻",
};

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        const options = { day: "numeric", month: "short", year: "numeric" };
        return d.toLocaleDateString("en-GB", options);
    } catch (e) {
        return dateStr;
    }
}

function formatDateShort(dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    } catch (e) {
        return dateStr;
    }
}

function daysUntil(dateStr) {
    if (!dateStr) return null;
    const scheduled = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    scheduled.setHours(0, 0, 0, 0);
    return Math.ceil((scheduled - today) / (24 * 60 * 60 * 1000));
}

function trimesterLabel(num) {
    if (num === 1) return "1st Trimester";
    if (num === 2) return "2nd Trimester";
    return "3rd Trimester";
}

/**
 * ANCScheduleBoard — Visual 8-contact schedule with grid and timeline views
 */
const ANCScheduleBoard = function(props) {
    const schedule = props.schedule || [];
    const summary = props.summary || {};
    const nextContact = props.nextContact;
    const onReschedule = props.onReschedule;
    const onRecordContact = props.onRecordContact;
    const loading = props.loading;
    const gestationalAge = props.gestationalAge;

    const _view = useState("grid");
    const view = _view[0];
    const setView = _view[1];

    if (loading) {
        return (
            <LoadingWrapper>
                <span>⏳</span> Loading schedule...
            </LoadingWrapper>
        );
    }

    if (!schedule || schedule.length === 0) {
        return (
            <EmptyState>
                <div className="empty-icon">📅</div>
                <div className="empty-title">No Schedule Available</div>
                <div className="empty-desc">
                    An ANC schedule will be generated once a booking with LNMP date is available.
                    The WHO 8-Contact model will be used to schedule visits at 12, 20, 26, 30, 34, 36, 38, and 40 weeks.
                </div>
            </EmptyState>
        );
    }

    return (
        <div>
            {/* ─── Progress Section ─── */}
            <ProgressSection>
                <ProgressLabel>
                    <span>
                        ANC Progress — {summary.attended || 0} of {summary.total || 8} contacts completed
                        {gestationalAge ? " · GA: " + gestationalAge.display + " weeks" : ""}
                    </span>
                    <span className="percent">{summary.progressPercent || 0}%</span>
                </ProgressLabel>
                <ProgressBarOuter>
                    <ProgressBarInner percent={summary.progressPercent || 0} />
                </ProgressBarOuter>
            </ProgressSection>

            {/* ─── Next Contact Banner ─── */}
            {nextContact && (
                <NextContactBanner>
                    <NextContactIcon>{nextContact.contact_number}</NextContactIcon>
                    <NextContactInfo>
                        <div className="next-label">Next Scheduled Contact</div>
                        <div className="next-detail">
                            Contact {nextContact.contact_number} — {nextContact.scheduled_gestational_age_weeks || nextContact.label || "—"} weeks
                        </div>
                        <div className="next-date">
                            📅 {formatDate(nextContact.scheduled_date)}
                            {daysUntil(nextContact.scheduled_date) !== null && (
                                <span> · {daysUntil(nextContact.scheduled_date) <= 0
                                    ? "Today or overdue"
                                    : daysUntil(nextContact.scheduled_date) + " days away"}</span>
                            )}
                        </div>
                    </NextContactInfo>
                    {onRecordContact && (
                        <NextContactAction onClick={function() { onRecordContact(nextContact); }}>
                            Record Contact
                        </NextContactAction>
                    )}
                </NextContactBanner>
            )}

            {/* ─── View Toggle + Legend ─── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "8px" }}>
                <ViewToggle>
                    <ViewToggleBtn active={view === "grid"} onClick={function() { setView("grid"); }}>
                        ▦ Grid View
                    </ViewToggleBtn>
                    <ViewToggleBtn active={view === "timeline"} onClick={function() { setView("timeline"); }}>
                        ▤ Timeline
                    </ViewToggleBtn>
                </ViewToggle>

                <Legend>
                    <LegendItem color="#43a047"><div className="legend-dot" /> Attended</LegendItem>
                    <LegendItem color="#ffa000"><div className="legend-dot" /> Upcoming</LegendItem>
                    <LegendItem color="#e53935"><div className="legend-dot" /> Missed</LegendItem>
                    <LegendItem color="#bdbdbd"><div className="legend-dot" /> Future</LegendItem>
                </Legend>
            </div>

            {/* ─── Grid View ─── */}
            {view === "grid" && (
                <ScheduleGrid>
                    {schedule.map(function(item) {
                        const status = item.derived_status || item.status || "future";
                        const days = daysUntil(item.scheduled_date);

                        return (
                            <ContactCard key={item.contact_number} status={status}>
                                <ContactCardHeader>
                                    <ContactNumber status={status}>
                                        {status === "attended" ? "✓" : item.contact_number}
                                    </ContactNumber>
                                    <StatusChip status={status}>
                                        {STATUS_ICONS[status] || "○"} {STATUS_LABELS[status] || "Scheduled"}
                                    </StatusChip>
                                </ContactCardHeader>

                                <ContactCardBody status={status}>
                                    <div className="card-ga">
                                        {item.scheduled_gestational_age_weeks
                                            ? item.scheduled_gestational_age_weeks + " weeks"
                                            : item.label || "Contact " + item.contact_number}
                                    </div>
                                    <div className="card-date">
                                        📅 {formatDate(item.scheduled_date)}
                                    </div>
                                    {item.actual_date && (
                                        <div className="card-actual">
                                            ✓ Attended: {formatDate(item.actual_date)}
                                        </div>
                                    )}
                                    {status === "upcoming" && days !== null && (
                                        <div className="card-date" style={{ color: "#e65100", fontWeight: 500 }}>
                                            {days <= 0 ? "Due today!" : "In " + days + " day" + (days > 1 ? "s" : "")}
                                        </div>
                                    )}
                                    {status === "missed" && (
                                        <div className="card-date" style={{ color: "#b71c1c", fontWeight: 500 }}>
                                            ⚠ {Math.abs(days || 0)} day{Math.abs(days || 0) !== 1 ? "s" : ""} overdue
                                        </div>
                                    )}
                                    <span className="card-trimester">
                                        {trimesterLabel(item.trimester)}
                                    </span>
                                </ContactCardBody>

                                <ContactCardFooter>
                                    {status !== "attended" && onReschedule && (
                                        <SmallActionBtn
                                            color="#1565c0"
                                            onClick={function() { onReschedule(item); }}
                                        >
                                            Reschedule
                                        </SmallActionBtn>
                                    )}
                                    {(status === "upcoming" || status === "missed") && onRecordContact && (
                                        <SmallActionBtn
                                            color="#43a047"
                                            onClick={function() { onRecordContact(item); }}
                                        >
                                            Record
                                        </SmallActionBtn>
                                    )}
                                </ContactCardFooter>
                            </ContactCard>
                        );
                    })}
                </ScheduleGrid>
            )}

            {/* ─── Timeline View ─── */}
            {view === "timeline" && (
                <TimelineContainer>
                    {schedule.map(function(item) {
                        const status = item.derived_status || item.status || "future";
                        const days = daysUntil(item.scheduled_date);

                        return (
                            <TimelineItem key={item.contact_number} status={status}>
                                <TimelineCard status={status}>
                                    <div className="tl-header">
                                        <span className="tl-title">
                                            Contact {item.contact_number} — {item.scheduled_gestational_age_weeks || "—"} weeks
                                        </span>
                                        <StatusChip status={status}>
                                            {STATUS_ICONS[status]} {STATUS_LABELS[status]}
                                        </StatusChip>
                                    </div>
                                    <div className="tl-details">
                                        <div className="tl-detail-item">
                                            📅 Scheduled: {formatDate(item.scheduled_date)}
                                        </div>
                                        {item.actual_date && (
                                            <div className="tl-detail-item" style={{ color: "#2e7d32" }}>
                                                ✓ Attended: {formatDate(item.actual_date)}
                                            </div>
                                        )}
                                        {status === "upcoming" && days !== null && (
                                            <div className="tl-detail-item" style={{ color: "#e65100", fontWeight: 500 }}>
                                                ⏱ {days <= 0 ? "Due today" : days + " days away"}
                                            </div>
                                        )}
                                        {status === "missed" && (
                                            <div className="tl-detail-item" style={{ color: "#b71c1c", fontWeight: 500 }}>
                                                ⚠ {Math.abs(days || 0)} days overdue
                                            </div>
                                        )}
                                        <div className="tl-detail-item">
                                            {trimesterLabel(item.trimester)}
                                        </div>
                                    </div>
                                    {status !== "attended" && (
                                        <div style={{ marginTop: "10px", display: "flex", gap: "8px" }}>
                                            {onReschedule && (
                                                <SmallActionBtn
                                                    color="#1565c0"
                                                    onClick={function() { onReschedule(item); }}
                                                >
                                                    Reschedule
                                                </SmallActionBtn>
                                            )}
                                            {(status === "upcoming" || status === "missed") && onRecordContact && (
                                                <SmallActionBtn
                                                    color="#43a047"
                                                    onClick={function() { onRecordContact(item); }}
                                                >
                                                    Record Contact
                                                </SmallActionBtn>
                                            )}
                                        </div>
                                    )}
                                </TimelineCard>
                            </TimelineItem>
                        );
                    })}
                </TimelineContainer>
            )}
        </div>
    );
};

export default ANCScheduleBoard;
